import React, { useEffect, useMemo, useRef, useState } from "react";
import classes from "./MisaPointSummary.module.scss";
import pointIcon from "../../static/images/common/point-vector.png";
import TopSummary from "../top-summary/TopSummary";
import TeacherComment from "../../molecules/teacher-commnet/TeacherComment";
import {
  fetchUserFortune,
  postUserFortune,
  UserFortuneResult,
} from "../../api/UserFortuneApi";
import rabbitHappy from "../../static/images/character/char-icon-happy.png";
import rabbitNormal from "../../static/images/character/normal.png";
import rabbitClapping from "../../static/images/character/clapping.png";
import mokomokoGray from "../../static/images/mokomoko-gray.png";
import fortuneCard1 from "../../static/images/point/fortune-card1.png";
import fortuneCard2 from "../../static/images/point/fortune-card2.png";
import fortuneCard3 from "../../static/images/point/fortune-card3.png";
import fortuneCardReverse1 from "../../static/images/point/fortune-card-reverse1.png";
import fortuneCardReverse2 from "../../static/images/point/fortune-card-reverse2.png";
import fortuneCardReverse3 from "../../static/images/point/fortune-card-reverse3.png";

type Props = {
  totalPoint: number;
  supportedPoint: number;
  increasedPoint: number;
  availableSupportPoint: number;
  isChildSelected: boolean;
  selectedPointOwnerId: number | null;
  onFortuneDrawn: () => void;
};

const fortuneCardFiles = [
  "fortune-card-reverse1.png",
  "fortune-card-reverse2.png",
  "fortune-card-reverse3.png",
];

// フリップ演出後に、結果表示へ切り替えるまでの待機時間
// CSS側のフリップアニメーション時間より少し長めにしておく
const FORTUNE_RESULT_DISPLAY_DELAY_MS = 5000;

/**
 * おみくじAPIのレスポンスを1件の結果オブジェクトに正規化する
 *
 * APIレスポンスが配列で返る場合は先頭要素を利用し、
 * 単体オブジェクトで返る場合はそのまま返す。
 */
const getFortuneResult = (
  data: UserFortuneResult | UserFortuneResult[] | null | undefined
) => {
  return Array.isArray(data) ? data[0] : data;
};

const MisaPointSummary = ({
  totalPoint,
  supportedPoint,
  increasedPoint,
  availableSupportPoint,
  isChildSelected,
  selectedPointOwnerId,
  onFortuneDrawn,
}: Props) => {
  // 当日のおみくじ結果を取得するAPI
  // すでにおみくじ済みの場合、結果カードと獲得ポイントを表示するために利用する
  const fetchFortuneApi = fetchUserFortune();

  // おみくじを引くためのPOST API
  // カード選択時に実行し、獲得ポイント・結果画像を取得する
  const postFortuneApi = postUserFortune();

  // フリップ演出完了後に結果表示へ切り替えるためのタイマー参照
  // unmount時や再実行時に clearTimeout できるように保持する
  const fortuneResultDisplayTimerRef = useRef<number | null>(null);

  // 表面のおみくじカード画像一覧
  // useMemoで固定化し、再レンダリング時に配列が作り直されないようにする
  const fortuneCards = useMemo(
    () => [fortuneCard1, fortuneCard2, fortuneCard3],
    []
  );

  // 裏面のおみくじ結果カード画像一覧
  // fortuneCards と同じ index で対応させる
  const fortuneCardReverses = useMemo(
    () => [fortuneCardReverse1, fortuneCardReverse2, fortuneCardReverse3],
    []
  );

  // ユーザーが選択したカードのindex
  // null の場合は、まだカード未選択
  const [selectedFortuneCardIndex, setSelectedFortuneCardIndex] = useState<
    number | null
  >(null);

  // 確定済みのおみくじ結果
  // 初回表示時にすでに引いている場合、またはフリップ演出後にここへセットする
  const [fortuneResult, setFortuneResult] = useState<UserFortuneResult | null>(
    null
  );

  // フリップ演出中に一時的に保持するおみくじ結果
  // 裏面カードにポイントを重ねて表示するために利用する
  const [pendingFortuneResult, setPendingFortuneResult] =
    useState<UserFortuneResult | null>(null);

  // ユーザーが選択した表面カード画像
  // selectedFortuneCardIndex が null の場合は未選択なので null
  const selectedFortuneCard =
    selectedFortuneCardIndex != null
      ? fortuneCards[selectedFortuneCardIndex]
      : null;

  // ユーザーが選択したカードに対応する裏面画像
  // フリップ後に表示されるカード画像として利用する
  const selectedFortuneCardReverse =
    selectedFortuneCardIndex != null
      ? fortuneCardReverses[selectedFortuneCardIndex]
      : null;

  // 確定済みのおみくじ結果に対応する裏面カード画像
  // APIから返却された resultImage と fortuneCardFiles を照合してindexを特定し、
  // そのindexに対応する fortuneCardReverses の画像を表示する
  const resultFortuneCard =
    fortuneResult?.resultImage != null
      ? fortuneCardReverses[
          fortuneCardFiles.findIndex(
            (file) => file === fortuneResult.resultImage
          )
        ]
      : null;

  // おみくじPOST中かどうか
  // true の間はカード連打防止やボタン無効化に利用する
  const isFortunePosting = postFortuneApi.state?.status === "processing";

  useEffect(() => {
    // 遅延表示タイマーが残っている場合は、古いポイント保有主体の結果表示を止める
    if (fortuneResultDisplayTimerRef.current !== null) {
      window.clearTimeout(fortuneResultDisplayTimerRef.current);
      fortuneResultDisplayTimerRef.current = null;
    }

    // ポイント保有主体が未選択の場合は、おみくじ状態もカード選択状態も初期化する
    if (selectedPointOwnerId === null) {
      setFortuneResult(null);
      setPendingFortuneResult(null);
      setSelectedFortuneCardIndex(null);
      return;
    }

    // ポイント保有主体を切り替えたら、前の保有主体のおみくじ結果を表示しないように初期化する
    setFortuneResult(null);
    setPendingFortuneResult(null);
    setSelectedFortuneCardIndex(null);
    // 選択中のポイント保有主体について、当日のおみくじ実施結果を取得する
    fetchFortuneApi.fetch({ pointOwnerId: selectedPointOwnerId });
  }, [selectedPointOwnerId]);

  useEffect(() => {
    return () => {
      // コンポーネント破棄後に遅延表示のsetStateが走らないようにする
      if (fortuneResultDisplayTimerRef.current !== null) {
        window.clearTimeout(fortuneResultDisplayTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    // GET APIが成功したタイミングだけ、画面表示用のおみくじ結果を反映する
    if (fetchFortuneApi.state?.status !== "success") {
      return;
    }

    // APIレスポンス形式の差異に備えて、配列・単体どちらでも結果オブジェクトに寄せる
    const result = getFortuneResult(
      fetchFortuneApi.state.data as unknown as UserFortuneResult | UserFortuneResult[]
    );
    // 当日実施済みの場合だけ結果表示に切り替え、未実施の場合はカード選択UIを表示する
    setFortuneResult(result?.isDrawnToday ? result : null);
  }, [fetchFortuneApi.state?.status]);

  useEffect(() => {
    // POST APIが成功したタイミングだけ、抽選結果を画面へ反映する
    if (postFortuneApi.state?.status !== "success") {
      return;
    }

    // APIレスポンス形式の差異に備えて、配列・単体どちらでも結果オブジェクトに寄せる
    const responseData = postFortuneApi.state.data as unknown as
      | UserFortuneResult
      | UserFortuneResult[]
      | null;
    const result = getFortuneResult(responseData);
    // DBに保存された結果画像名から、画面表示するカード画像のindexを特定する
    const resultImage = result?.resultImage;
    const resultIndex = fortuneCardFiles.findIndex((file) => file === resultImage);
    if (resultIndex >= 0) {
      setSelectedFortuneCardIndex(resultIndex);
    }
    // フリップ後の裏面カードにポイントを描画するため、結果表示への切り替え前に一時結果を保持する
    setPendingFortuneResult(result?.isDrawnToday ? result : null);
    // フリップ演出が完了するまでは選択カード表示を維持し、その後に結果表示へ切り替える
    if (fortuneResultDisplayTimerRef.current !== null) {
      window.clearTimeout(fortuneResultDisplayTimerRef.current);
    }
    fortuneResultDisplayTimerRef.current = window.setTimeout(() => {
      setFortuneResult(result?.isDrawnToday ? result : null);
      setPendingFortuneResult(null);
      fortuneResultDisplayTimerRef.current = null;
    }, FORTUNE_RESULT_DISPLAY_DELAY_MS);

    // 獲得ポイントが反映された最新ポイントを親画面で再取得する
    onFortuneDrawn();
    // POST結果の再処理を防ぐため、API状態を初期化する
    postFortuneApi.reset();
  }, [postFortuneApi.state?.status]);

  const handleFortuneCardClick = (index: number) => {
    // ポイント保有主体未選択時、またはPOST処理中は二重送信を防ぐ
    if (selectedPointOwnerId === null || isFortunePosting) {
      return;
    }

    // 選択直後は演出用に選択カードを表示し、既存結果表示は一度解除する
    setFortuneResult(null);
    setPendingFortuneResult(null);
    setSelectedFortuneCardIndex(index);
    // 選択したカードに対応する結果画像名をAPIへ渡して、おみくじ結果を登録する
    postFortuneApi.post({
      pointOwnerId: selectedPointOwnerId,
      resultImage: fortuneCardFiles[index],
    });
  };

  return (
    <section
      className={`${classes.pointPanel} ${
        isChildSelected ? classes.childSelectedPointPanel : ""
      }`}
    >
      {isChildSelected && (
        <div className={classes.fortunePanel}>
          <div className={classes.summary}>
            <TopSummary
              leftLogo={rabbitHappy}
              mainTitle="今日のおみくじ!"
              rightLogo={mokomokoGray}
            />
          </div>
          <div className={classes.teacherComment}>
            {fortuneResult?.isDrawnToday ? (
              <TeacherComment
                image={rabbitClapping}
                comment={[
                  `やったね、${fortuneResult.acquiredPoint.toLocaleString("ja-JP")}ptゲット！`,
                  "また明日も遊んでね!",
                ]}
                charLeft
              />
            ) : (
              <TeacherComment
                image={rabbitNormal}
                comment={["カードをえらんで", "ポイントをゲット！"]}
                charRight
              />
            )}
          </div>
          {fortuneResult?.isDrawnToday && resultFortuneCard ? (
            <div className={classes.fortuneResultPanel}>
              <div className={classes.fortuneResultNotice}>
                毎日23時におみくじ更新！
              </div>
              <div className={classes.fortuneResultCardWrap}>
                <img
                  className={classes.fortuneResultCard}
                  src={resultFortuneCard}
                  alt="おみくじ結果カード"
                />
                <div className={classes.fortuneResultPoint}>
                  {fortuneResult.acquiredPoint.toLocaleString("ja-JP")}pt
                </div>
              </div>
            </div>
          ) : selectedFortuneCard && selectedFortuneCardReverse ? (
            <div className={classes.selectedFortuneCardPanel}>
              <div className={classes.fortuneCardFlipShell}>
                <div className={classes.fortuneCardFlipper}>
                  <img
                    className={`${classes.selectedFortuneCard} ${classes.front}`}
                    src={selectedFortuneCard}
                    alt={`選択したおみくじカード${
                      (selectedFortuneCardIndex ?? 0) + 1
                    }`}
                  />
                  <div className={`${classes.selectedFortuneCard} ${classes.back}`}>
                    <img
                      className={classes.selectedFortuneCardImage}
                      src={selectedFortuneCardReverse}
                      alt={`おみくじ結果カード${
                        (selectedFortuneCardIndex ?? 0) + 1
                      }`}
                    />
                    {pendingFortuneResult?.isDrawnToday && (
                      <div className={classes.selectedFortuneCardPoint}>
                        {pendingFortuneResult.acquiredPoint.toLocaleString("ja-JP")}pt
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className={classes.fortuneCards}>
              {fortuneCards.map((card, index) => (
                <button
                  key={`fortune-card-${index + 1}`}
                  type="button"
                  className={classes.fortuneCardButton}
                  onClick={() => handleFortuneCardClick(index)}
                  disabled={selectedPointOwnerId === null || isFortunePosting}
                >
                  <img
                    className={classes.fortuneCard}
                    src={card}
                    alt={`おみくじカード${index + 1}`}
                  />
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      <div className={classes.pointPanelTitle}>
        <img className={classes.pointPanelIcon} src={pointIcon} alt="" />
        <span>全保有ポイント</span>
        <img className={classes.pointPanelIcon} src={pointIcon} alt="" />
      </div>
      <div className={classes.pointPanelBody}>
        <div className={classes.pointAmount}>
          <span className={classes.pointNumber}>
            {totalPoint.toLocaleString("ja-JP")}
          </span>
          <span className={classes.pointUnit}>pt</span>
        </div>
        <div className={classes.pointBreakdownLabel}>-内訳-</div>
        <div className={classes.pointRows}>
          <div className={classes.pointRow}>
            <span>応援したポイント</span>
            <span>{supportedPoint.toLocaleString("ja-JP")}pt</span>
          </div>
          <div className={`${classes.pointRow} ${classes.accent}`}>
            <span>余っているポイント</span>
            <span>{availableSupportPoint.toLocaleString("ja-JP")}pt</span>
          </div>
          <div className={`${classes.pointRow} ${classes.realizedProfit}`}>
            <span>運用で増えたポイント</span>
            <span>{increasedPoint.toLocaleString("ja-JP")}pt</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MisaPointSummary;
